<script setup lang="ts">
import {onMounted, ref} from "vue";
import {MedicineCabinetCategory} from "@/core/interfaces/medicine-cabinet/MedicineCabinet";
import {medicineCabinetApi} from "@/container";
import {useRoute} from "vue-router";
import Loader from "@/core/components/base/spinner/Loader.vue";
import MedicineCabinetCategoryAdvice from "@/medicine-cabinet/components/MedicineCabinetCategoryAdvice.vue";
import HeaderBackButton from "@/core/components/header-back-button/HeaderBackButton.vue";
import Product from "@/product/interfaces/Product";
import MedicineCabinetCategoryProducts from "@/medicine-cabinet/components/MedicineCabinetCategoryProducts.vue";
import MedicineCabinetCategoryProductAdvice
    from "@/medicine-cabinet/components/MedicineCabinetCategoryProductAdvice.vue";

const category = ref<MedicineCabinetCategory>();
const products = ref<Product[]>();
const loading = ref<boolean>(false);
const route = useRoute();

onMounted(() => {
    loading.value = true;
    Promise.all([loadCategory(), loadProducts()])
        .finally(() => loading.value = false);
});

/**
 * Load medicine cabinet category.
 */
const loadCategory = async () => {
    category.value = await medicineCabinetApi().readCategory(Number(route.params.categoryId));
};

/**
 * Load medicine cabinet category products.
 */
const loadProducts = async () => {
    products.value = await medicineCabinetApi().indexProducts(Number(route.params.categoryId));
};

/**
 * Action when products are selected.
 */
const onProductSelected = async (productIds: number[]) => {
    await medicineCabinetApi().syncProducts(Number(route.params.categoryId), productIds);
    await loadProducts();
};
</script>

<template>
  <div v-if="loading" class="flex flex-1 items-center justify-center">
    <Loader />
  </div>
  <div v-if="category">
    <header-back-button :title="category.name" class="bg-transparent" />
    <div class="p-4">
      <MedicineCabinetCategoryAdvice :category="category" />
    </div>
    <div class="m-4 rounded-lg overflow-hidden">
      <MedicineCabinetCategoryProductAdvice :category-id="category.id" :featured-products="products || []" @product-selected="onProductSelected" />
      <MedicineCabinetCategoryProducts
        :category="category"
        :selected-products="products"
        @product-selected="onProductSelected"
      />
    </div>
  </div>
</template>
